import {
  HealthCoverageAnalysis,
  HealthCoverageCategory,
  HealthCoverageStatus,
  DuplicateRisk,
  StructureWarning,
  DuplicateWarning,
  HealthCoverageUIRow,
  HealthCoverageLayerGroup,
  CoverageLayer,
  CategoryImportance,
  StatusColor,
} from "../interfaces/DTO/insurance_data/HealthCoverageAnalysis";
import { formatInsuranceProductType, formatHealthSubCategory } from "./insuranceUtil";
import { InsuranceProductType, HealthSubCategory } from "../interfaces/DTO/insurance_data/InsuranceData";

// ─── Layer assignments ────────────────────────────────────────────────────────

const CATEGORY_LAYER: Record<HealthCoverageCategory, CoverageLayer> = {
  SURGERIES: "CORE",
  TRANSPLANTS: "CORE",
  MEDICATIONS: "CORE",
  CONSULTATION_DIAGNOSIS: "SERVICES",
  AMBULATORY: "SERVICES",
  CARE_MANAGEMENT: "SERVICES",
  DIGITAL_SERVICES: "EXTENSIONS",
  ALT_MEDICINE: "EXTENSIONS",
  SPECIAL_SERVICES: "EXTENSIONS",
  CRITICAL_ILLNESS: "EXTENSIONS",
  PERSONAL_ACCIDENTS: "EXTENSIONS",
  DENTAL: "EXTENSIONS",
};

const LAYER_LABELS: Record<CoverageLayer, string> = {
  CORE: "ליבה רפואית",
  SERVICES: "שירותים רפואיים",
  EXTENSIONS: "הרחבות",
  REVIEW: "דורש בדיקה",
};

// Display labels for the top-level analysis categories (e.g. "ניתוחים", "השתלות").
// These categories are NOT stored in the DB — they are the logical buckets that
// the backend's analyze_health_coverage() assigns each coverage item to at query time,
// based on _RAW_TO_CATEGORY and _SUBCOVERAGE_SCHEMA in insurance_data_service.py.
// HealthCoverageCategory is a TypeScript string-literal union (not an enum) whose values
// match the keys returned by the API in the categories object.
export const CATEGORY_LABELS: Record<HealthCoverageCategory, string> = {
  SURGERIES: "ניתוחים",
  TRANSPLANTS: "השתלות",
  MEDICATIONS: "תרופות",
  AMBULATORY: "אמבולטורי",
  CONSULTATION_DIAGNOSIS: "ייעוץ ואבחון",
  DIGITAL_SERVICES: "שירותים דיגיטליים",
  ALT_MEDICINE: "רפואה משלימה",
  CARE_MANAGEMENT: "ניהול וליווי רפואי",
  SPECIAL_SERVICES: "שירותים מיוחדים",
  CRITICAL_ILLNESS: "מחלות קשות",
  PERSONAL_ACCIDENTS: "תאונות אישיות",
  DENTAL: "שיניים",
};

const LAYER_ORDER: CoverageLayer[] = ["CORE", "SERVICES", "EXTENSIONS", "REVIEW"];

const CATEGORY_ORDER: HealthCoverageCategory[] = [
  "SURGERIES", "TRANSPLANTS", "MEDICATIONS",
  "CONSULTATION_DIAGNOSIS", "AMBULATORY", "CARE_MANAGEMENT",
  "DIGITAL_SERVICES", "ALT_MEDICINE", "SPECIAL_SERVICES",
  "CRITICAL_ILLNESS", "PERSONAL_ACCIDENTS", "DENTAL",
];

// ─── Category importance ──────────────────────────────────────────────────────

const CATEGORY_IMPORTANCE: Record<HealthCoverageCategory, CategoryImportance> = {
  SURGERIES: "CORE",
  TRANSPLANTS: "CORE",
  MEDICATIONS: "CORE",
  CONSULTATION_DIAGNOSIS: "SUPPORT",
  AMBULATORY: "SUPPORT",
  CARE_MANAGEMENT: "SUPPORT",
  DIGITAL_SERVICES: "OPTIONAL",
  ALT_MEDICINE: "OPTIONAL",
  SPECIAL_SERVICES: "OPTIONAL",
  CRITICAL_ILLNESS: "OPTIONAL",
  PERSONAL_ACCIDENTS: "OPTIONAL",
  DENTAL: "OPTIONAL",
};

function categoryIcon(status: HealthCoverageStatus, importance: CategoryImportance): string {
  if (status === "HAS")     return "✅";
  if (status === "PARTIAL") return "⚠️";
  if (status === "UNKNOWN") return "⚠️";
  // MISSING
  if (importance === "CORE") return "❌";
  return "◻️";
}

// ─── Status display ───────────────────────────────────────────────────────────

const STATUS_LABELS: Record<HealthCoverageStatus, string> = {
  HAS: "יש",
  MISSING: "חסר",
  PARTIAL: "חלקי",
  UNKNOWN: "לא ידוע",
};

// ─── Build functions ──────────────────────────────────────────────────────────

export function buildHealthCoverageLayerGroups(analysis: HealthCoverageAnalysis): HealthCoverageLayerGroup[] {
  const rows = buildHealthCoverageUIRows(analysis);
  const byLayer: Record<CoverageLayer, HealthCoverageUIRow[]> = { CORE: [], SERVICES: [], EXTENSIONS: [], REVIEW: [] };
  for (const row of rows) byLayer[row.layer].push(row);
  return LAYER_ORDER
    .filter((l) => byLayer[l].length > 0)
    .map((l) => ({ layer: l, label: LAYER_LABELS[l], rows: byLayer[l] }));
}

export function buildHealthCoverageUIRows(analysis: HealthCoverageAnalysis): HealthCoverageUIRow[] {
  const warningsByCategory: Record<string, DuplicateWarning[]> = {};
  for (const w of analysis.summary.duplicate_warnings) {
    if (!warningsByCategory[w.category]) warningsByCategory[w.category] = [];
    warningsByCategory[w.category].push(w);
  }

  return CATEGORY_ORDER
    .map((key) => {
      const cat = analysis.categories[key];
      if (!cat) return null;
      const layer = CATEGORY_LAYER[key];
      const categoryImportance = CATEGORY_IMPORTANCE[key];
      const isCore = layer === "CORE";
      const missingIsCritical = isCore && cat.overall_status === "MISSING";
      const structureWarning: StructureWarning = cat.structure_warning ?? "NONE";
      return {
        categoryKey: key,
        label: CATEGORY_LABELS[key] ?? key,
        layer,
        categoryImportance,
        overallStatus: cat.overall_status,
        statusLabel: STATUS_LABELS[cat.overall_status],
        statusColor: resolveStatusColor(cat.overall_status, cat.duplicate_risk, isCore),
        icon: categoryIcon(cat.overall_status, categoryImportance),
        subCoverages: cat.sub_coverages ?? [],
        duplicateRisk: cat.duplicate_risk,
        structureWarning,
        derivedCoverage: cat.derived_coverage ?? null,
        structureWarningMessage: cat.structure_warning_message ?? null,
        warnings: warningsByCategory[key] ?? [],
        isCore,
        missingIsCritical,
      } as HealthCoverageUIRow;
    })
    .filter(Boolean) as HealthCoverageUIRow[];
}

function resolveStatusColor(status: HealthCoverageStatus, risk: DuplicateRisk, isCore: boolean): StatusColor {
  if (risk === "LIKELY") return "warning";
  if (risk === "POSSIBLE" && status === "HAS") return "warning";
  if (status === "MISSING") return isCore ? "error" : "default";
  if (status === "PARTIAL") return "warning";
  if (status === "UNKNOWN") return "info";
  return "success";
}

// ─── Formatters ───────────────────────────────────────────────────────────────

export function formatCoverageItemLabel(raw: string): string {
  if (!raw) return raw;
  const asProduct = formatInsuranceProductType(raw as InsuranceProductType);
  if (asProduct !== raw) return asProduct;
  const asHealth = formatHealthSubCategory(raw as HealthSubCategory);
  if (asHealth !== raw) return asHealth;
  return raw;
}

export function formatDuplicateRisk(risk: DuplicateRisk): string {
  return ({ NONE: "", POSSIBLE: "חפיפה", LIKELY: "כפול" } as const)[risk];
}

export function subCoverageIcon(status: HealthCoverageStatus, importance: string): string {
  if (status === "HAS") return "✅";
  if (status === "UNKNOWN") return "❓";
  // MISSING — אייקון לפי חשיבות
  if (importance === "CORE") return "❌";
  return "◻️";  // SUPPORT / OPTIONAL / UNKNOWN — לא שלילי
}

export function subCoverageTextColor(status: HealthCoverageStatus, importance: string): string {
  if (status === "HAS") return "text.primary";
  if (importance === "CORE") return "error.main";
  if (importance === "SUPPORT") return "text.secondary";
  return "text.disabled";
}
